import {Toggle} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Snackbar} from '@dropbox/dig-components/snackbar';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, OpenLine} from '@dropbox/dig-icons/assets';
import {GlobalHeader} from '@dropbox/dpc-ia';
import SpritesGptBright from 'assets/SpritesGptBright.svg';
import SpritesGptDark from 'assets/SpritesGptDark.svg';
import {setToken} from 'components/auth/Auth';
import {ROUTE_PATHS} from 'constant';
import {useTheme} from 'hooks/use_theme';
import {useUserPermissions} from 'hooks/use_user_permissions';
import {useNavigateToTop} from 'hooks/useNavigateToTop';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {useAppSelector} from 'store';
import {getPulseUser} from 'store/features/auth/selectors';
import {
  getDropboxService,
  getLoginService,
  isLocalDevelopment,
  isSandboxMode,
  LOGOUT_URL,
} from 'utilities';

import styles from './Layout.module.css';
import {ProfileAvatar} from './shared/ProfileAvatar';

type RefreshType =
  | 'dropboxers'
  | 'feedback'
  | 'reviews'
  | 'reviewCycles'
  | 'feedbackRequests'
  | null;

type RefreshEnvironment = 'prod' | 'sandbox' | 'both';

export const FEEDBACK_URL = 'https://dbx.link/sprites-help';

const SPRITES_GPT_GUIDE = 'https://dbx.link/spritesgpt-guide';
const WORKDAY = 'https://dbx.link/sprites-hub';

export const TopNavbar = () => {
  const {t} = useTranslation();
  const isDarkMode = useTheme() === 'dark';
  const navigate = useNavigateToTop();
  const curUser = useAppSelector(getPulseUser);
  const isLoggedIn = curUser !== null;
  const {isSuperuser} = useUserPermissions();
  const isSandboxToggled = isSandboxMode();
  // Dry-run tooling is dev-only. Requiring a non-production build (NODE_ENV is a
  // compile-time constant) lets the bundler dead-code-eliminate it from production
  // builds, and the localhost check keeps it off any deployed host at runtime.
  const isDev = process.env.NODE_ENV !== 'production' && isLocalDevelopment();

  const showAvatarMenu =
    curUser?.display_name && curUser?.abbreviated_name && curUser?.email;

  const [refreshLoading, setRefreshLoading] = useState<RefreshType>(null);
  // Prod and sandbox are independent toggles (either, both, or neither).
  const [targetProd, setTargetProd] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('refresh_target_prod') !== 'false';
    } catch {
      return true;
    }
  });
  const [targetSandbox, setTargetSandbox] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('refresh_target_sandbox') !== 'false';
    } catch {
      return true;
    }
  });
  // Dry-run is a dev-only capability (the backend rejects it outside dev).
  const [dryRun, setDryRun] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({open: false, message: ''});

  // Map the two independent toggles onto the API's single environment enum.
  // `null` means nothing is selected, in which case refreshes are disabled.
  const refreshEnv: RefreshEnvironment | null =
    targetProd && targetSandbox
      ? 'both'
      : targetProd
      ? 'prod'
      : targetSandbox
      ? 'sandbox'
      : null;

  const showSnackbar = useCallback((message: string) => {
    setSnackbar({open: true, message});
  }, []);

  const toggleTarget = useCallback((env: 'prod' | 'sandbox') => {
    const setter = env === 'prod' ? setTargetProd : setTargetSandbox;
    const key =
      env === 'prod' ? 'refresh_target_prod' : 'refresh_target_sandbox';
    setter((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(key, String(next));
      } catch {
        /* no-op */
      }
      return next;
    });
  }, []);

  const handleLogout = () => {
    getLoginService()
      .routeLogoutAndRemoveCookieApiV1AuthLogoutGet()
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        setToken('');
        window.location.href = LOGOUT_URL;
      });
  };

  const triggerRefresh = useCallback(
    async (
      type: NonNullable<RefreshType>,
      serviceFn: () => Promise<unknown>,
      label: string
    ) => {
      setRefreshLoading(type);
      try {
        await serviceFn();
        const envLabel =
          refreshEnv === 'both' ? 'prod and sandbox' : refreshEnv;
        const dryRunLabel = isDev && dryRun ? ' (dry run)' : '';
        showSnackbar(`${label} import started for ${envLabel}${dryRunLabel}`);
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Unknown error';
        console.error(`${label} refresh failed:`, err);
        showSnackbar(
          `Failed to start ${label.toLowerCase()} import: ${detail}`
        );
      } finally {
        setRefreshLoading(null);
      }
    },
    [showSnackbar, refreshEnv, dryRun, isDev]
  );

  // Dry-run is only honored in dev; force false everywhere else.
  const effectiveDryRun = isDev && dryRun;

  const handleDropboxerRefresh = () => {
    if (!refreshEnv) return;
    triggerRefresh(
      'dropboxers',
      () =>
        getDropboxService().refreshAllDropboxersApiV1DropboxerRefreshPut(
          refreshEnv,
          effectiveDryRun
        ),
      'Dropboxer'
    );
  };

  const handleFeedbackRefresh = () => {
    if (!refreshEnv) return;
    triggerRefresh(
      'feedback',
      () =>
        getDropboxService().refreshAllFeedbackApiV1DropboxerRefreshFeedbackPut(
          refreshEnv,
          effectiveDryRun
        ),
      'Feedback'
    );
  };

  const handleReviewsRefresh = () => {
    if (!refreshEnv) return;
    triggerRefresh(
      'reviews',
      () =>
        getDropboxService().refreshAllReviewsApiV1DropboxerRefreshReviewsPut(
          refreshEnv,
          effectiveDryRun
        ),
      'Reviews'
    );
  };

  const handleReviewCyclesRefresh = () => {
    if (!refreshEnv) return;
    triggerRefresh(
      'reviewCycles',
      () =>
        getDropboxService().refreshAllReviewCyclesApiV1DropboxerRefreshReviewCyclesPut(
          refreshEnv,
          effectiveDryRun
        ),
      'Review cycles'
    );
  };

  const handleFeedbackRequestsRefresh = () => {
    if (!refreshEnv) return;
    triggerRefresh(
      'feedbackRequests',
      () =>
        getDropboxService().refreshAllFeedbackRequestsApiV1DropboxerRefreshFeedbackRequestsPut(
          refreshEnv,
          effectiveDryRun
        ),
      'Feedback requests'
    );
  };

  return (
    <GlobalHeader.Wrapper shouldAlwaysShowLogo>
      <Link to="/">
        <img src={isDarkMode ? SpritesGptDark : SpritesGptBright} />
      </Link>
      <GlobalHeader.UtilityNav>
        {isLoggedIn && isSuperuser && (
          <div className={styles['sandbox-mode-toggle']}>
            <span
              style={{
                fontFamily: 'sans-serif',
                color: '#4a4a4a',
                fontSize: '12px',
              }}
            >
              Sandbox Mode
            </span>
            <Toggle
              aria-label="Sandbox mode"
              isToggled={isSandboxToggled}
              disabled={false}
              wrapperProps={{style: {marginRight: 12}}}
              onChange={(next) => {
                const nextToggled =
                  typeof next === 'boolean' ? next : !isSandboxToggled;
                try {
                  if (nextToggled) {
                    window.localStorage.setItem('sandbox_mode', 'true');
                  } else {
                    window.localStorage.removeItem('sandbox_mode');
                  }
                } catch (_) {
                  /* no-op */
                }
                // Reload to ensure queries include is_sandbox and data is refreshed
                window.location.reload();
              }}
            />
          </div>
        )}
        <GlobalHeader.Help>
          <Menu.Segment>
            <Menu.ActionItem
              onClick={() => {
                navigate(ROUTE_PATHS.PROFILE);
              }}
            >
              Opt-Out settings
            </Menu.ActionItem>
            <Menu.ActionItem
              href={FEEDBACK_URL}
              tagName="a"
              target="_blank"
              withRightAccessory={<UIIcon src={OpenLine} />}
            >
              SPRiTEs Help
            </Menu.ActionItem>
            <Menu.ActionItem
              href={SPRITES_GPT_GUIDE}
              tagName="a"
              target="_blank"
              withRightAccessory={<UIIcon src={OpenLine} />}
            >
              SPRiTEs-GPT Guide
            </Menu.ActionItem>
            <Menu.ActionItem
              href={WORKDAY}
              tagName="a"
              target="_blank"
              withRightAccessory={<UIIcon src={OpenLine} />}
            >
              Workday
            </Menu.ActionItem>
          </Menu.Segment>
          {isLoggedIn && isSuperuser && (
            <>
              <Menu.Segment withLabel="Import target">
                {/*
                 * Rendered as Menu.Row (not Menu.ActionItem) so selecting a
                 * target/dry-run does NOT close the menu: ActionItem calls the
                 * menu's setSelected() which closes it, while Row only fires
                 * onClick. GlobalHeader.Help does not expose closeOnSelection.
                 */}
                <Menu.Row
                  interactive
                  role="menuitemcheckbox"
                  aria-checked={targetProd}
                  onClick={() => toggleTarget('prod')}
                  withLeftAccessory={
                    targetProd ? <UIIcon src={CheckmarkLine} /> : undefined
                  }
                >
                  Prod
                </Menu.Row>
                <Menu.Row
                  interactive
                  role="menuitemcheckbox"
                  aria-checked={targetSandbox}
                  onClick={() => toggleTarget('sandbox')}
                  withLeftAccessory={
                    targetSandbox ? <UIIcon src={CheckmarkLine} /> : undefined
                  }
                >
                  Sandbox
                </Menu.Row>
                {isDev && (
                  <Menu.Row
                    interactive
                    role="menuitemcheckbox"
                    aria-checked={dryRun}
                    onClick={() => setDryRun((prev) => !prev)}
                    withLeftAccessory={
                      dryRun ? <UIIcon src={CheckmarkLine} /> : undefined
                    }
                  >
                    Dry run (dev only)
                  </Menu.Row>
                )}
              </Menu.Segment>
              <Menu.Segment withLabel="Admin">
                <Menu.ActionItem
                  onClick={handleDropboxerRefresh}
                  disabled={refreshLoading !== null || refreshEnv === null}
                >
                  {refreshLoading === 'dropboxers'
                    ? 'Importing dropboxers...'
                    : 'Import dropboxers from Workday'}
                </Menu.ActionItem>
                <Menu.ActionItem
                  onClick={handleFeedbackRefresh}
                  disabled={refreshLoading !== null || refreshEnv === null}
                >
                  {refreshLoading === 'feedback'
                    ? 'Importing feedback...'
                    : 'Import feedback from Workday'}
                </Menu.ActionItem>
                <Menu.ActionItem
                  onClick={handleReviewsRefresh}
                  disabled={refreshLoading !== null || refreshEnv === null}
                >
                  {refreshLoading === 'reviews'
                    ? 'Importing reviews...'
                    : 'Import reviews from Workday'}
                </Menu.ActionItem>
                <Menu.ActionItem
                  onClick={handleReviewCyclesRefresh}
                  disabled={refreshLoading !== null || refreshEnv === null}
                >
                  {refreshLoading === 'reviewCycles'
                    ? 'Importing review cycles...'
                    : 'Import review cycles from Workday'}
                </Menu.ActionItem>
                <Menu.ActionItem
                  onClick={handleFeedbackRequestsRefresh}
                  disabled={refreshLoading !== null || refreshEnv === null}
                >
                  {refreshLoading === 'feedbackRequests'
                    ? 'Importing feedback requests...'
                    : 'Import feedback requests from Workday'}
                </Menu.ActionItem>
              </Menu.Segment>
            </>
          )}
        </GlobalHeader.Help>
        {isLoggedIn && showAvatarMenu && (
          <GlobalHeader.AccountMenu
            src={curUser?.profile_photo_url ?? undefined}
            initials={curUser?.abbreviated_name ?? undefined}
          >
            <Menu.Segment>
              <Menu.ActionItem
                withLeftAccessory={
                  <ProfileAvatar
                    abbreviated_name={curUser?.abbreviated_name ?? undefined}
                    display_name={curUser?.display_name || ''}
                    profile_photo_url={curUser?.profile_photo_url ?? undefined}
                  />
                }
                onClick={() => navigate(ROUTE_PATHS.PROFILE)}
                withSubtitle={curUser?.email}
                withTitle={curUser?.display_name}
              ></Menu.ActionItem>
            </Menu.Segment>
            <Menu.Segment>
              <Menu.ActionItem onClick={handleLogout}>
                {t('sign_out')}
              </Menu.ActionItem>
            </Menu.Segment>
          </GlobalHeader.AccountMenu>
        )}
      </GlobalHeader.UtilityNav>
      <Snackbar.Position>
        <Snackbar
          open={snackbar.open}
          onRequestClose={() => setSnackbar({open: false, message: ''})}
          timeout={5000}
        >
          <Snackbar.Message>{snackbar.message}</Snackbar.Message>
        </Snackbar>
      </Snackbar.Position>
    </GlobalHeader.Wrapper>
  );
};
